<template>
  <v-form ref="alertForm">
    <v-layout row wrap px-3 v-resize="onResize">
      <v-flex xs12 sm4 lg5>
        <v-text-field :label="$t('admin.alerts.rule_name')" :placeholder="$t('admin.alerts.rule_name_placeholder')"
                      :value="alert.name" @input="nameInput"
                      :rules="[
                      v=>!!v.trim() || $t('admin.alerts.validation.name_required'),
                      v=>v.length < 256 || $t('admin.alerts.validation.name_too_long')]"/>
      </v-flex>
      <v-flex xs4 sm3 lg3 pt-3 class="alertSwitchContainer">
        <v-switch class="alertSwitch" :label="$t('admin.alerts.active')" v-model="isActive"/>
      </v-flex>

      <v-flex xs8 sm5 lg4 text-xs-right>
        {{$t('admin.alerts.affect_before')}}
        <animated-counter :number="alert.affectedStudents"/>
        {{$t('admin.alerts.affect_after')}}
        <v-flex text-xs-right>
          <span class="caption">{{$t('admin.alerts.last_updated', {lastUpdated: alert.lastUpdated})}}</span>
        </v-flex>
      </v-flex>


      <v-flex xs12 py-3>
        <v-divider/>
      </v-flex>
      <div class="filter-panel-outer" ref="filterPanelOuter">
        <div class="filter-panel-inner pr-4">
          <h3 class="pb-3">{{$t('filters.title')}}</h3>

          <v-flex xs12 pb-4>
            <v-flex>
              <h4>{{$t('admin.alerts.courses')}}</h4>
            </v-flex>
            <v-btn color="primary" @click="selectHierarchy">
              <v-icon left>mdi-file-tree</v-icon>
              <span>{{$t('admin.alerts.select_courses')}}</span>
              <v-icon right>mdi-play</v-icon>
            </v-btn>
          </v-flex>

          <v-flex xs12 pb-5>
            <v-flex>
              <h4>{{$t('admin.alerts.engagement')}}</h4>
            </v-flex>
            <v-flex px-3>
              <vue-slider ref="slider" :value="alert.sliderValues" :data="arrayData" :piecewiseLabel="true"
                          tooltip="hover" :tooltip-dir="'top'" :processStyle="sliderStyle"
                          :tooltipStyle="tooltipStyle" v-on:input="sliderInput" :lazy="true" :show="isOpen">
                <div slot="label" slot-scope="state" :class="[sliderCircle(state)]">
                  {{state.label}}
                </div>
              </vue-slider>
            </v-flex>
          </v-flex>

          <v-flex xs12 pb-3>
            <v-flex>
              <h4>{{$t('admin.alerts.duration')}}</h4>
            </v-flex>
            <v-flex px-3 class="noOfDays">
              <v-text-field class="days-input" type="number" min="1" :prefix="$t('admin.alerts.for')"
                            :suffix="$t('admin.alerts.days')" :value="alert.days"
                            @input="daysInput" :rules="[v=> v>0 || $t('admin.alerts.validation.alert_days')]">
              </v-text-field>
            </v-flex>
          </v-flex>

          <v-flex xs12 pb-0>
            <v-flex>
              <h4>{{$t('admin.alerts.date_range_description')}}</h4>
            </v-flex>
            <v-flex px-2>
              <date-range-picker :dateRange="alert.dateRange" :indefinite="true" :now="null" :id="id"
                                :vertical="isMobile"
                                 update-start="SET_ADMIN_ALERT_START_DATE" update-end="SET_ADMIN_ALERT_END_DATE"
                                 @updated="dateRangeUpdated"/>
            </v-flex>
          </v-flex>

          <alert-filter-container :id="id" :alert="alert"/>
        </div>
      </div>
      <div class="content-panel">
        <v-layout row wrap>
          <v-btn color="primary" @click="createNewEmail(id)">{{$t('admin.alerts.buttons.create_email')}}</v-btn>
          <transition-group tag="div" name="list" class="flex xs12">
            <email-editor class="flex xs12" v-for="email in emails(id)" :key="email.id" :email="email"
                          :slider-values="alert.sliderValues"
                          :days="alert.days"/>
          </transition-group>
        </v-layout>
      </div>
    </v-layout>
  </v-form>
</template>

<script>
  import vueSlider from 'vue-slider-component'
  import DateRangePicker from '../../date/DateRangePicker'
  import DatePicker from '../../date/DatePicker'
  import AnimatedCounter from './AnimatedCounter'
  import EmailEditor from './EmailEditor'
  import AlertFilterContainer from './AlertFilterContainer'
  import {
    SET_ADMIN_ALERT_ACTIVE,
    SET_ADMIN_ALERT_DATE_RANGE,
    SET_ADMIN_ALERT_DAYS,
    SET_ADMIN_ALERT_NAME,
    SET_ADMIN_ALERT_SLIDER,
    SET_ALERT_EDITING,
    SET_ALERT_HIERARCHY_DRAWER_STATE,
    SET_CLASS_HIERARCHY_SELECTIONS
  } from '../../../store/mutation-types'
  import { mapActions, mapGetters, mapMutations } from 'vuex'
  import { GET_ADMIN_ALERT_EMAILS } from '../../../store/getter-types'
  import { CREATE_ADMIN_ALERT_EMAIL_TEMPLATE, SEND_ADMIN_ALERT_UPDATE } from '../../../store/action-types'
  import TooltipStyle from '../../../mixins/TooltipStyle'

  export default {
    components: {
      EmailEditor,
      AnimatedCounter,
      DatePicker,
      DateRangePicker,
      vueSlider,
      AlertFilterContainer
    },
    name: 'alert-body',
    mixins: [TooltipStyle],
    props: {
      alert: {
        type: Object,
        required: true
      },
      isOpen: {
        type: Boolean,
        required: true
      },
      id: {
        required: true
      }
    },
    data () {
      return {
        state: false,
        arrayData: ['N', 'V', 'L', 'P', 'G', 'H'],
        format: 'YYYY-MM-DD',
        ignore: true
      }
    },
    methods: {
      sliderInput (values) {
        // Value is an array that looks like ['low', 'high'] always two values and always in order.
        this.updateSlider({sliderValues: values, id: this.id})
        this.getAffectedStudentCount()
      },
      daysInput (days) {
        this.updateInput({days: days, id: this.id})
        this.getAffectedStudentCount()
      },
      dateRangeUpdated (dateRange) {
        this.updateDateRange({dateRange: dateRange, id: this.id})
        this.getAffectedStudentCount()
      },
      nameInput (text) {
        this.updateName({name: text, id: this.id})
        this.getAffectedStudentCount()
      },
      getAffectedStudentCount () {
        if (!this.ignore && this.$refs.alertForm.validate()) {
          this.isActive = false
          this.updateAlert(this.id)
        }
      },
      selectHierarchy () {
        this.setAlertEditing(this.alert.id)
        this.setClasses(this.alert.courses)
        this.displayTreeDrawer(true)
      },
      onResize () {
        setTimeout(() => {
          this.$refs.slider.refresh()
        }, 10)
      },
      ...mapMutations({
        updateSlider: SET_ADMIN_ALERT_SLIDER,
        updateInput: SET_ADMIN_ALERT_DAYS,
        updateDateRange: SET_ADMIN_ALERT_DATE_RANGE,
        updateName: SET_ADMIN_ALERT_NAME,
        displayTreeDrawer: SET_ALERT_HIERARCHY_DRAWER_STATE,
        setAlertEditing: SET_ALERT_EDITING,
        setClasses: SET_CLASS_HIERARCHY_SELECTIONS
        /*,
       setTreeAlert: SET_TREE_ALERT */
      }),
      ...mapActions({
        updateAlert: SEND_ADMIN_ALERT_UPDATE,
        createNewEmail: CREATE_ADMIN_ALERT_EMAIL_TEMPLATE /* ,
        loadCoursesTree: GET_COURSES_TREE */
      }),
      sliderCircle (state) {
        return `slider-circle slider-circle-${state.label}`
      }
    },
    computed: {
      isActive: {
        get () {
          return this.alert.isActive
        },
        set (value) {
          this.$store.commit(SET_ADMIN_ALERT_ACTIVE, {isActive: value, id: this.id})
          this.updateAlert(this.id)
        }
      },
      isMobile () {
        return this.$vuetify.breakpoint.xsOnly
      },
      ...mapGetters({
        emails: GET_ADMIN_ALERT_EMAILS
      })
    },
    watch: {
      isOpen () {
        this.onResize()
      }
    },
    mounted () {
      this.onResize()
      setTimeout(() => {
        // Some timeout while the sliders initialise for the first time. The sliders change their value to the one
        // from the store so this helps with not sending unnecessary update requests to the server.
        this.ignore = false
      }, 2000)
    }
  }
</script>

<style scoped>
.days-input > > > input {
  text-align: center;
  -moz-appearance: textfield;
}
.slider-circle {
  color: white;
  font-weight: bold;
  font-size: 12px;
  border-radius: 10px;
  padding-left: 6px;
  padding-top: 1px;
  margin: 14px 0px 0px -7px;
  width: 20px;
  height: 20px;
}
.slider-circle-N {
  background-color: #9E9E9E;
}
.slider-circle-V {
  background-color: #F44336;
}
.slider-circle-L {
  background-color: #FF9800;
}
.slider-circle-P {
  background-color: #BA68C8;
}
.slider-circle-G {
  background-color: #03A9F4;
}
.slider-circle-H {
  background-color: #4CAF50;
}
.filter-panel-outer {
  flex: 3 3 0;
}
.filter-panel-inner {
  position: -webkit-sticky;
  position: sticky;
  top: 64px;
}
.content-panel {
  flex: 10 10 0;
}
.noOfDays {
  width: 140px
}
@media (min-width: 600px) {
  .alertSwitchContainer {
    padding-left: 24px;
  }
}
  .days-input {
    padding: 0;
  }
</style>



// WEBPACK FOOTER //
// src/components/settings/alert/AlertBody.vue